import React, { useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import { debounce } from "lodash/fp";
import { some } from "lodash";
import { AddressCreate, ClientCreate, CompanyTypeEnum } from "@/graphql/generated/graphql";
import useCreateClient from "@/hooks/useCreateClient";
import OverwatchModal from "../OverwatchModal";
import ClientFields from "./ClientFields";
import NewAddressesForm from "@/shared-components/NewAddressesForm";

const normalDebounce = debounce(500);

interface CreateClientModalProps {
  handleClose: () => void;
  clientName?: string;
  onSuccess?: (client?: { id: string; name: string } | null) => void;
}

const CreateClientModal: React.FC<CreateClientModalProps> = ({ handleClose, clientName, onSuccess }) => {
  const debounceFunc = useRef(normalDebounce((fn) => fn())).current;

  const [createClient, { loading }] = useCreateClient({
    onSuccess,
    handleClose,
    visible: true,
  });

  const [client, setClient] = useState<ClientCreate>({
    companyName: clientName || "",
    phone: "",
    email: "",
    companyNumber: "",
    tradingName: "",
    vatNumber: "",
    companyType: CompanyTypeEnum.Ltd,
    address: undefined
  });

  const handleOnChange = <K extends keyof ClientCreate>(key: K, value: ClientCreate[K]) => {
    debounceFunc.cancel();
    debounceFunc(() => {
      setClient((prev) => ({
        ...prev,
        [key]: value,
      }));
    });
  };

  const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    // De-structure for cleaner mutation call
    const { address, ...rest } = client;

    createClient({
      variables: {
        client: {
          ...rest,
          visible: true,
          tradingName: client.tradingName || undefined,
          vatNumber: client.vatNumber || undefined,
          ...(address && { address }),
        },
      },
    });
  };

  const disableSave = () => {
    if (loading) return true;

    const { companyName, companyNumber, address } = client;
    if (address?.street && address?.street.trim() !== "") {
      return some([companyName, companyNumber, address?.street, address?.zip], (val) => !val || val.trim() === "");
    }
    return !companyName?.trim() || !companyNumber?.trim();
  };

  // took help from Codex, works but don't quite fully get how and why
  const addresses = useMemo(
    () =>
      client.address
        ? [
            {
              ...client.address,
              title: client.address.title ?? undefined,
              locationType: client.address.locationType ?? undefined,
            },
          ]
        : undefined,
    [client.address]
  );

  return (
    <OverwatchModal
      size="sm"
      title="New Client Form"
      caption="Fill out the details below to create a new client"
      handleClose={handleClose}
      primaryButtonAction={handleSubmit}
      primaryButtonText="Create"
      disablePrimary={disableSave()}
      secondaryButtonText="Cancel"
      secondaryButtonAction={handleClose}
    >
      <Box component="form" noValidate onSubmit={handleSubmit}>
        <ClientFields clientName={clientName} client={client} setClient={setClient} />
        <Box mt={4}>
          <NewAddressesForm
            addresses={addresses}
            onChange={(addresses) => handleOnChange('address', addresses[0] as AddressCreate)}
          />
        </Box>
      </Box>
    </OverwatchModal>
  );
};

export default CreateClientModal;
